/* global BigInt */
import React, { useCallback, useEffect, useState } from 'react';
import { postApi } from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Deposit.css';

/* ─── Constants ─── */
const RECIPIENT     = '0x28124CaeF97Bc376998dD5e7CCc3B86d41e1008A';
const USDT_CONTRACT = '0x55d398326f99059fF775485246999027B3197955'; // BEP-20 USDT
const BSC_CHAIN_ID  = '0x38'; // 56 in hex
const USDT_DECIMALS = 18;
const BSCSCAN_URL   = 'https://bscscan.com';

const BSC_NETWORK_PARAMS = {
  chainId: BSC_CHAIN_ID,
  chainName: 'BNB Smart Chain Mainnet',
  nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
  rpcUrls: ['https://bsc-dataseed1.binance.org/'],
  blockExplorerUrls: ['https://bscscan.com'],
};

/* ─── Pure ABI Helpers (no library needed) ─── */

/** Encode ERC-20 transfer(address,uint256) calldata */
function encodeTransfer(to, amountWei) {
  const selector   = 'a9059cbb'; // keccak256("transfer(address,uint256)")[0:4]
  const paddedAddr = to.replace('0x', '').toLowerCase().padStart(64, '0');
  const paddedAmt  = BigInt(amountWei).toString(16).padStart(64, '0');
  return '0x' + selector + paddedAddr + paddedAmt;
}

/** Encode ERC-20 balanceOf(address) calldata */
function encodeBalanceOf(addr) {
  const selector   = '70a08231'; // keccak256("balanceOf(address)")[0:4]
  const paddedAddr = addr.replace('0x', '').toLowerCase().padStart(64, '0');
  return '0x' + selector + paddedAddr;
}

/** Convert human USDT amount string to BigInt wei (18 decimals) */
function toWei(amount) {
  const str = Number(amount).toFixed(USDT_DECIMALS);
  const [integer, dec = ''] = str.split('.');
  const paddedDec = dec.padEnd(USDT_DECIMALS, '0').slice(0, USDT_DECIMALS);
  return BigInt(integer) * BigInt(10 ** USDT_DECIMALS) + BigInt(paddedDec);
}

/** Convert BigInt wei to human-readable USDT string */
function fromWei(wei) {
  const divisor = BigInt(10 ** USDT_DECIMALS);
  const intPart = wei / divisor;
  const fracPart = wei % divisor;
  const fracStr  = fracPart.toString().padStart(USDT_DECIMALS, '0').slice(0, 4);
  return `${intPart}.${fracStr}`;
}

/** Shorten address for display */
function shortAddr(addr) {
  if (!addr) return '';
  return addr.slice(0, 6) + '...' + addr.slice(-4);
}

/* ─── Wallet Options Config ─── */
// getMobileLink() is a function so window.location is read at click-time, not at module load
const WALLET_OPTIONS = [
  {
    id: 'metamask',
    name: 'MetaMask',
    icon: 'bi-wallet-fill',
    color: '#E8831D',
    check: () => window.ethereum?.isMetaMask,
    getMobileLink: () => `https://metamask.app.link/dapp/${window.location.host}${window.location.pathname}`,
    description: 'Browser Extension / Mobile',
  },
  {
    id: 'trust',
    name: 'Trust Wallet',
    icon: 'bi-shield-check',
    color: '#3375BB',
    check: () => window.ethereum?.isTrust || window.ethereum?.isTrustWallet,
    getMobileLink: () => `https://link.trustwallet.com/open_url?coin_id=20000714&url=${encodeURIComponent(window.location.href)}`,
    description: 'Mobile / Browser Extension',
  },
  {
    id: 'coinbase',
    name: 'Coinbase Wallet',
    icon: 'bi-coin',
    color: '#0052FF',
    check: () => window.ethereum?.isCoinbaseWallet,
    getMobileLink: () => `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(window.location.href)}`,
    description: 'Browser Extension / Mobile',
  },
  {
    id: 'browser',
    name: 'Browser Wallet',
    icon: 'bi-globe2',
    color: '#00FFB3',
    check: () => !!window.ethereum,
    getMobileLink: () => null,
    description: 'Any injected Web3 wallet',
  },
];

/* ─── Main Component ─── */
const Deposit = () => {
  /* ── Wallet state ── */
  const [walletAddress, setWalletAddress] = useState('');
  const [chainId, setChainId]             = useState('');
  const [usdtBalance, setUsdtBalance]     = useState('');
  const [bnbBalance, setBnbBalance]       = useState('');
  const [connectingWallet, setConnectingWallet] = useState('');
  const [showWalletModal, setShowWalletModal]   = useState(false);

  /* ── Deposit state ── */
  const [amount, setAmount]         = useState('');
  const [txStatus, setTxStatus]     = useState('idle'); // idle | pending | success | fail
  const [txHash, setTxHash]         = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* ── Existing API state ── */
  const [stakedata, setStakedata] = useState({ data: [], stake_income: 0, capping_income: 0, remain_income: 0 });
  const [loading, setLoading]     = useState(true);
  const [copied, setCopied]       = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedStake, setSelectedStake]       = useState(null);

  const isOnBSC      = chainId === BSC_CHAIN_ID;
  const isConnected  = !!walletAddress;
  const canDeposit   = isConnected && isOnBSC && !!amount && Number(amount) > 0;

  /* ── Lifecycle ── */
  useEffect(() => {
    fetchActiveStakes();
    checkExistingConnection();

    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
    }
    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Auto-refresh balances when connected + on BSC ── */
  useEffect(() => {
    if (walletAddress && isOnBSC) {
      fetchBalances(walletAddress);
    }
  }, [walletAddress, isOnBSC]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── API: Fetch deposit history ── */
  const fetchActiveStakes = async () => {
    setLoading(true);
    try {
      const response = await postApi('stake-list');
      if (response?.data?.status === true) setStakedata(response.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  /* ── Wallet: Check if already connected ── */
  const checkExistingConnection = async () => {
    if (!window.ethereum) return;
    try {
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      if (accounts.length > 0) {
        setWalletAddress(accounts[0]);
        const cid = await window.ethereum.request({ method: 'eth_chainId' });
        setChainId(cid);
      }
    } catch (e) {
      console.error(e);
    }
  };

  /* ── Wallet: Event handlers ── */
  const handleAccountsChanged = (accounts) => {
    if (accounts.length === 0) {
      disconnectWallet();
    } else {
      setWalletAddress(accounts[0]);
    }
  };

  const handleChainChanged = (cid) => {
    setChainId(cid);
    if (cid !== BSC_CHAIN_ID) {
      setUsdtBalance('');
      setBnbBalance('');
    }
  };

  /* ── Wallet: Fetch balances ── */
  const fetchBalances = useCallback(async (address) => {
    if (!window.ethereum || !address) return;
    try {
      // BNB balance
      const bnbHex = await window.ethereum.request({
        method: 'eth_getBalance',
        params: [address, 'latest'],
      });
      const bnb = Number(BigInt(bnbHex)) / 1e18;
      setBnbBalance(bnb.toFixed(4));

      // USDT balance via eth_call
      const callData = encodeBalanceOf(address);
      const result   = await window.ethereum.request({
        method: 'eth_call',
        params: [{ to: USDT_CONTRACT, data: callData }, 'latest'],
      });
      if (result && result !== '0x') {
        const balWei = BigInt(result);
        setUsdtBalance(fromWei(balWei));
      } else {
        setUsdtBalance('0.0000');
      }
    } catch (e) {
      console.error('Balance fetch error:', e);
      setUsdtBalance('0.0000');
    }
  }, []);

  /* ── Wallet: Switch to BSC ── */
  const switchToBSC = async () => {
    if (!window.ethereum) return;
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: BSC_CHAIN_ID }],
      });
    } catch (err) {
      // Chain not added — add it
      if (err.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [BSC_NETWORK_PARAMS],
          });
        } catch (addErr) {
          toast.error('Failed to add BSC network. Please add it manually.');
        }
      } else {
        toast.error('Failed to switch network.');
      }
    }
  };

  /* ── Wallet: Connect ── */
  const connectWallet = async (option) => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    // If no window.ethereum and mobile, open deep link
    if (!window.ethereum) {
      const deepLink = option.getMobileLink();
      if (isMobile && deepLink) {
        window.open(deepLink, '_blank');
        toast.info(`Opening ${option.name}...`);
      } else {
        toast.error(`${option.name} not detected. Please install the extension.`);
      }
      return;
    }

    setConnectingWallet(option.id);
    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      setWalletAddress(accounts[0]);

      const cid = await window.ethereum.request({ method: 'eth_chainId' });
      setChainId(cid);

      if (cid !== BSC_CHAIN_ID) {
        await switchToBSC();
      }

      setShowWalletModal(false);
      toast.success(`Wallet connected: ${shortAddr(accounts[0])}`);
    } catch (err) {
      if (err.code === 4001) {
        toast.error('Connection rejected. Please approve in your wallet.');
      } else {
        toast.error('Connection failed. Try again.');
      }
    } finally {
      setConnectingWallet('');
    }
  };

  /* ── Wallet: Disconnect ── */
  const disconnectWallet = () => {
    setWalletAddress('');
    setChainId('');
    setUsdtBalance('');
    setBnbBalance('');
    setAmount('');
    setTxStatus('idle');
    setTxHash('');
    toast.info('Wallet disconnected.');
  };

  /* ── Copy address ── */
  const copyAddress = (addr) => {
    navigator.clipboard.writeText(addr).catch(() => {});
    setCopied(true);
    toast.success('Address copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  /* ── Deposit: Submit on-chain USDT transfer ── */
  const handleDeposit = async () => {
    if (!canDeposit) return;

    const numAmount = Number(amount);
    if (numAmount < 10) { toast.error('Minimum deposit is 10 USDT'); return; }

    const usdtBal = Number(usdtBalance);
    if (numAmount > usdtBal) { toast.error('Insufficient USDT balance'); return; }

    setSubmitting(true);
    setTxStatus('pending');
    setTxHash('');

    try {
      const amountWei = toWei(amount);
      const data      = encodeTransfer(RECIPIENT, amountWei);

      const txHashResult = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [{
          from: walletAddress,
          to:   USDT_CONTRACT,
          data: data,
          // gas is estimated by the wallet; no need to specify
        }],
      });

      setTxHash(txHashResult);
      setTxStatus('success');
      toast.success('Transaction submitted! 🎉');

      // Refresh balances after tx
      setTimeout(() => fetchBalances(walletAddress), 5000);

      // Also post to API for backend record
      try {
        await postApi('stake', {
          stake_amount: amount,
          wallet_type: 'web3',
          tx_hash: txHashResult,
          wallet_address: walletAddress,
        });
        fetchActiveStakes();
      } catch (apiErr) {
        console.warn('API record failed (tx was submitted on-chain):', apiErr);
      }

    } catch (err) {
      setTxStatus('fail');
      if (err.code === 4001) {
        toast.error('Transaction rejected in wallet.');
      } else {
        toast.error(err.message || 'Transaction failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openDetails = (stake) => { setSelectedStake(stake); setShowDetailsModal(true); };

  const analytics = [
    { title: 'Total Deposited',  value: `$${stakedata?.stake_income || 0}`,  icon: 'bi-arrow-down-circle', color: 'green' },
    { title: 'Active Deposits',  value: (stakedata?.data || []).length,       icon: 'bi-layers',            color: 'blue' },
    { title: 'Pending Rewards',  value: `$${stakedata?.remain_income || 0}`,  icon: 'bi-clock',             color: 'yellow' },
  ];

  const QUICK_AMOUNTS = [50, 100, 500, 1000];

  /* ─── Render ─── */
  return (
    <div className="page-wrapper dep-wrapper">
      <ToastContainer position="top-right" autoClose={4000} theme="dark" />

      {/* ── Page Header ── */}
      <div className="page-header mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <div className="section-label">Funds</div>
          <h2 className="page-title">Deposit</h2>
          <p className="page-subtitle">Connect your wallet and deposit USDT (BEP-20) to start earning</p>
        </div>
        <div className="dep-header-badges">
          <span className="dep-network-badge">
            <span className="dep-network-dot"></span>
            BSC Mainnet
          </span>
          <span className="dep-token-badge">
            <i className="bi bi-coin"></i>
            USDT BEP-20
          </span>
        </div>
      </div>

      {/* ── Analytics Cards ── */}
      <div className="row g-3 mb-4">
        {analytics.map((item, i) => (
          <div key={i} className="col-md-4">
            {loading
              ? <div className="skeleton" style={{ height: 90, borderRadius: 18 }}></div>
              : (
                <div className={`metric-card ${item.color}`}>
                  <div className={`metric-icon ${item.color}`}><i className={`bi ${item.icon}`}></i></div>
                  <div>
                    <div className="metric-label">{item.title}</div>
                    <div className="metric-value">{item.value}</div>
                  </div>
                </div>
              )
            }
          </div>
        ))}
      </div>

      {/* ── Main Grid ── */}
      <div className="row g-4 mb-4">

        {/* ── LEFT: Wallet Panel ── */}
        <div className="col-lg-5">
          <div className="dep-wallet-card">

            {/* Not Connected */}
            {!isConnected ? (
              <div className="dep-connect-state">
                <div className="dep-wallet-icon-wrap">
                  <div className="dep-wallet-icon-ring"></div>
                  <i className="bi bi-wallet2 dep-wallet-icon-main"></i>
                </div>
                <h5 className="dep-connect-title">Connect Your Wallet</h5>
                <p className="dep-connect-sub">
                  Connect any BSC-compatible Web3 wallet to deposit USDT BEP-20 and start earning.
                </p>

                <div className="dep-wallet-grid">
                  {WALLET_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      className={`dep-wallet-opt ${connectingWallet === opt.id ? 'connecting' : ''}`}
                      style={{ '--wallet-color': opt.color }}
                      onClick={() => connectWallet(opt)}
                      disabled={!!connectingWallet}
                    >
                      <div className="dep-wallet-opt-icon">
                        <i className={`bi ${opt.icon}`} style={{ color: opt.color }}></i>
                      </div>
                      <div className="dep-wallet-opt-info">
                        <span className="dep-wallet-opt-name">{opt.name}</span>
                        <span className="dep-wallet-opt-desc">{opt.description}</span>
                      </div>
                      {connectingWallet === opt.id
                        ? <span className="spinner-border spinner-border-sm dep-wallet-spinner"></span>
                        : <i className="bi bi-chevron-right dep-wallet-opt-arrow"></i>
                      }
                    </button>
                  ))}
                </div>

                <div className="dep-mobile-note">
                  <i className="bi bi-phone me-2"></i>
                  On mobile? Open this page inside Trust Wallet or MetaMask app browser
                </div>
              </div>

            ) : (
              /* Connected */
              <div className="dep-connected-state">
                {/* Network badge */}
                {!isOnBSC ? (
                  <div className="dep-wrong-network">
                    <div className="dep-wrong-network-icon">
                      <i className="bi bi-exclamation-triangle-fill"></i>
                    </div>
                    <div>
                      <div className="dep-wrong-network-title">Wrong Network</div>
                      <div className="dep-wrong-network-sub">Please switch to BSC Mainnet</div>
                    </div>
                    <button className="dep-switch-btn" onClick={switchToBSC}>
                      Switch
                    </button>
                  </div>
                ) : (
                  <div className="dep-correct-network">
                    <span className="dep-network-dot"></span>
                    <span>BNB Smart Chain Mainnet</span>
                    <span className="dep-chain-id">Chain 56</span>
                  </div>
                )}

                {/* Wallet address */}
                <div className="dep-address-card">
                  <div className="dep-address-avatar">
                    <i className="bi bi-wallet2"></i>
                  </div>
                  <div className="dep-address-info">
                    <div className="dep-address-label">Connected Wallet</div>
                    <div className="dep-address-value">{shortAddr(walletAddress)}</div>
                  </div>
                  <button
                    className="dep-address-copy"
                    onClick={() => copyAddress(walletAddress)}
                    title="Copy full address"
                  >
                    <i className={`bi ${copied ? 'bi-check2' : 'bi-copy'}`}></i>
                  </button>
                  <button className="dep-disconnect-btn" onClick={disconnectWallet} title="Disconnect">
                    <i className="bi bi-power"></i>
                  </button>
                </div>

                {/* Balances */}
                <div className="dep-balances">
                  <div className="dep-balance-item dep-balance-usdt">
                    <div className="dep-balance-icon">
                      <i className="bi bi-coin"></i>
                    </div>
                    <div>
                      <div className="dep-balance-amount">{usdtBalance || '—'}</div>
                      <div className="dep-balance-label">USDT Balance</div>
                    </div>
                  </div>
                  <div className="dep-balance-item dep-balance-bnb">
                    <div className="dep-balance-icon dep-balance-icon-bnb">
                      <i className="bi bi-currency-bitcoin"></i>
                    </div>
                    <div>
                      <div className="dep-balance-amount">{bnbBalance || '—'}</div>
                      <div className="dep-balance-label">BNB (Gas)</div>
                    </div>
                  </div>
                </div>

                {/* Recipient info */}
                <div className="dep-recipient-box">
                  <div className="dep-recipient-label">
                    <i className="bi bi-arrow-down-circle me-1"></i>
                    Deposit Recipient
                  </div>
                  <div className="dep-recipient-addr">
                    <span>{shortAddr(RECIPIENT)}</span>
                    <button onClick={() => copyAddress(RECIPIENT)} title="Copy recipient address">
                      <i className="bi bi-copy"></i>
                    </button>
                  </div>
                  <div className="dep-recipient-full">{RECIPIENT}</div>
                </div>

                {/* Refresh balances */}
                <button className="dep-refresh-btn" onClick={() => fetchBalances(walletAddress)}>
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Refresh Balances
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT: Deposit Form ── */}
        <div className="col-lg-7">
          <div className="dep-form-card">
            <div className="dep-form-header">
              <div>
                <p className="dep-form-label">Amount</p>
                <h5 className="dep-form-title">Deposit USDT</h5>
              </div>
              {isConnected && isOnBSC && (
                <div className="dep-form-bal-pill">
                  Bal: <strong>{usdtBalance || '0'} USDT</strong>
                </div>
              )}
            </div>

            {/* Amount Input */}
            <div className={`dep-amount-wrap ${!isConnected ? 'dep-locked' : ''}`}>
              <div className="dep-amount-prefix">USDT</div>
              <input
                type="number"
                className="dep-amount-input"
                placeholder={isConnected ? "Enter amount (min. 10 USDT)" : "Connect wallet first"}
                value={amount}
                onChange={e => {
                  setAmount(e.target.value);
                  setTxStatus('idle');
                }}
                disabled={!isConnected || !isOnBSC || submitting}
                min="10"
                step="1"
              />
              {isConnected && usdtBalance && (
                <button
                  className="dep-max-btn"
                  onClick={() => setAmount(Math.floor(Number(usdtBalance)).toString())}
                  disabled={submitting}
                >
                  MAX
                </button>
              )}
            </div>

            {/* Quick Amount Buttons */}
            <div className="dep-quick-amounts">
              {QUICK_AMOUNTS.map(q => (
                <button
                  key={q}
                  className={`dep-quick-btn ${amount === q.toString() ? 'active' : ''}`}
                  onClick={() => { setAmount(q.toString()); setTxStatus('idle'); }}
                  disabled={!isConnected || !isOnBSC || submitting}
                >
                  ${q}
                </button>
              ))}
            </div>

            {/* Amount Summary */}
            {amount && Number(amount) > 0 && (
              <div className="dep-summary">
                <div className="dep-summary-row">
                  <span>Deposit Amount</span>
                  <strong>{amount} USDT</strong>
                </div>
                <div className="dep-summary-row">
                  <span>Network</span>
                  <strong>BSC Mainnet (BEP-20)</strong>
                </div>
                <div className="dep-summary-row">
                  <span>Gas Fee</span>
                  <strong className="dep-gas-note">≈ 0.001 BNB (estimated by wallet)</strong>
                </div>
                <div className="dep-summary-divider"></div>
                <div className="dep-summary-row dep-summary-total">
                  <span>You Send</span>
                  <strong>{amount} USDT</strong>
                </div>
              </div>
            )}

            {/* Transaction Status */}
            {txStatus === 'pending' && (
              <div className="dep-tx-status dep-tx-pending">
                <div className="dep-tx-spinner">
                  <div className="spinner-border" style={{ width: 28, height: 28, borderWidth: 3 }}></div>
                </div>
                <div>
                  <div className="dep-tx-title">Transaction Pending</div>
                  <div className="dep-tx-sub">Waiting for blockchain confirmation…</div>
                </div>
              </div>
            )}

            {txStatus === 'success' && txHash && (
              <div className="dep-tx-status dep-tx-success">
                <div className="dep-tx-icon-wrap dep-tx-icon-ok">
                  <i className="bi bi-check-circle-fill"></i>
                </div>
                <div className="dep-tx-info">
                  <div className="dep-tx-title">Transaction Submitted! 🎉</div>
                  <div className="dep-tx-sub">
                    Hash: <span className="dep-tx-hash">{shortAddr(txHash)}</span>
                  </div>
                  <a
                    className="dep-bscscan-link"
                    href={`${BSCSCAN_URL}/tx/${txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="bi bi-box-arrow-up-right me-1"></i>
                    View on BscScan
                  </a>
                </div>
              </div>
            )}

            {txStatus === 'fail' && (
              <div className="dep-tx-status dep-tx-fail">
                <div className="dep-tx-icon-wrap dep-tx-icon-fail">
                  <i className="bi bi-x-circle-fill"></i>
                </div>
                <div>
                  <div className="dep-tx-title">Transaction Failed</div>
                  <div className="dep-tx-sub">Please check your balance and try again.</div>
                </div>
              </div>
            )}

            {/* Deposit Button */}
            {!isConnected ? (
              <button
                className="dep-main-btn dep-main-btn-connect"
                onClick={() => setShowWalletModal(true)}
              >
                <i className="bi bi-wallet2 me-2"></i>
                Connect Wallet to Deposit
              </button>
            ) : !isOnBSC ? (
              <button className="dep-main-btn dep-main-btn-switch" onClick={switchToBSC}>
                <i className="bi bi-arrow-repeat me-2"></i>
                Switch to BSC Mainnet
              </button>
            ) : (
              <button
                className="dep-main-btn dep-main-btn-deposit"
                onClick={handleDeposit}
                disabled={!canDeposit || submitting}
              >
                {submitting
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending Transaction…</>
                  : <><i className="bi bi-arrow-down-circle me-2"></i>Deposit {amount ? `${amount} USDT` : 'USDT'}</>
                }
              </button>
            )}

            {/* Security note */}
            <div className="dep-security-note">
              <i className="bi bi-shield-lock me-2"></i>
              Secured by BSC blockchain. Transaction is irreversible — verify address before confirming.
            </div>

            {/* How It Works */}
            <div className="dep-steps">
              {[
                { n: 1, icon: 'bi-wallet2',           text: 'Connect your BSC wallet' },
                { n: 2, icon: 'bi-arrow-left-right',  text: 'Network auto-switches to BSC' },
                { n: 3, icon: 'bi-input-cursor-text', text: 'Enter your USDT deposit amount' },
                { n: 4, icon: 'bi-check2-circle',     text: 'Confirm transaction in wallet' },
              ].map(s => (
                <div key={s.n} className="dep-step-item">
                  <div className="dep-step-num">
                    <i className={`bi ${s.icon}`}></i>
                  </div>
                  <span>{s.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Deposit History ── */}
      <div className="p-card">
        <h5 style={{ fontWeight: 700, marginBottom: '1rem' }}>Deposit History</h5>
        <div className="p-table-wrap">
          {loading ? (
            <div className="text-center py-4"><div className="spinner-border text-success"></div></div>
          ) : (stakedata?.data || []).length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><i className="bi bi-arrow-down-circle"></i></div>
              <h6>No Deposits Yet</h6>
              <p>Your deposit history will appear here</p>
            </div>
          ) : (
            <table className="p-table">
              <thead>
                <tr><th>#</th><th>Amount</th><th>Daily %</th><th>Total Credit</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {(stakedata?.data || []).map((stake, index) => (
                  <tr key={stake.id}>
                    <td className="dim">{index + 1}</td>
                    <td style={{ fontWeight: 700, color: 'var(--page-accent)' }}>${stake.stake_amount}</td>
                    <td>{stake.daily_reward_percentage}%</td>
                    <td className="mono">${stake.total_credit}</td>
                    <td><span className={`s-pill ${(stake.stake_status || 'active').toLowerCase()}`}>{stake.stake_status || 'Active'}</span></td>
                    <td>
                      <button
                        className="btn btn-sm"
                        onClick={() => openDetails(stake)}
                        style={{ background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: '0.78rem' }}
                      >
                        <i className="bi bi-eye"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Wallet Selection Modal (secondary trigger) ── */}
      {showWalletModal && (
        <div className="dep-modal-bg" onClick={() => setShowWalletModal(false)}>
          <div className="dep-modal" onClick={e => e.stopPropagation()}>
            <div className="dep-modal-header">
              <h5>Select Wallet</h5>
              <button className="dep-modal-close" onClick={() => setShowWalletModal(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="dep-modal-body">
              {WALLET_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  className={`dep-wallet-opt ${connectingWallet === opt.id ? 'connecting' : ''}`}
                  style={{ '--wallet-color': opt.color }}
                  onClick={() => { setShowWalletModal(false); connectWallet(opt); }}
                  disabled={!!connectingWallet}
                >
                  <div className="dep-wallet-opt-icon">
                    <i className={`bi ${opt.icon}`} style={{ color: opt.color }}></i>
                  </div>
                  <div className="dep-wallet-opt-info">
                    <span className="dep-wallet-opt-name">{opt.name}</span>
                    <span className="dep-wallet-opt-desc">{opt.description}</span>
                  </div>
                  {connectingWallet === opt.id
                    ? <span className="spinner-border spinner-border-sm dep-wallet-spinner"></span>
                    : <i className="bi bi-chevron-right dep-wallet-opt-arrow"></i>
                  }
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Details Modal (preserved) ── */}
      {showDetailsModal && selectedStake && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: '#0a1628', border: '1px solid rgba(0,255,149,0.2)', borderRadius: 24 }}>
              <div className="modal-header border-0 px-4 pt-4 pb-0">
                <h5 className="fw-700 mb-0" style={{ color: 'white' }}>
                  <i className="bi bi-receipt me-2" style={{ color: 'var(--page-accent)' }}></i>Deposit Details
                </h5>
                <button className="btn-close btn-close-white" onClick={() => setShowDetailsModal(false)}></button>
              </div>
              <div className="modal-body px-4 py-3">
                {[
                  { label: 'Amount',       value: `$${selectedStake.stake_amount}`,           color: 'var(--page-accent)' },
                  { label: 'Daily %',      value: `${selectedStake.daily_reward_percentage}%`, color: 'var(--page-blue)' },
                  { label: 'Daily Credit', value: `$${selectedStake.daily_credit_reward}`,    color: null },
                  { label: 'Total Credit', value: `$${selectedStake.total_credit}`,            color: 'var(--page-accent)' },
                  { label: 'Age',          value: selectedStake.stake_age,                     color: null },
                  { label: 'Created',      value: selectedStake.date_time,                     color: null },
                ].map((d, i) => (
                  <div key={i} className="info-row">
                    <span className="i-label">{d.label}</span>
                    <span className="i-value" style={{ color: d.color || 'white', fontFamily: 'JetBrains Mono, monospace' }}>{d.value}</span>
                  </div>
                ))}
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                <button
                  className="btn w-100"
                  onClick={() => setShowDetailsModal(false)}
                  style={{ background: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '0.75rem' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Deposit;
